import Select from '../../Select';
import CompactEventTypeSelect from '../CompactEventTypeSelect';
import OptionToggle from '../../OptionToggle.jsx';
import React from 'react';
import TextInput from '../../TextInput';
import {
    useBusinessInfo,
    useCustomFieldsSchemaQuery,
    useEventTypesQuery,
} from '../../../lib/Queries';
import CompactBookableResourcesSelect from '~/components/scheduling/CompactBookableResourcesSelect.jsx';
import SchedulingFilterTypeSettings from '~/components/forms/edit/components/SchedulingFilterTypeSettings';
import HidePriceDurationSetting from '~/components/forms/edit/components/HidePriceDurationSetting';
import HideSpotsLeftSetting from '~/components/forms/edit/components/HideSpotsLeftSetting';
import QuantityOptionSettings from '~/components/forms/edit/components/QuantityOptionSettings';
import QuantityLabelSetting from '~/components/forms/edit/components/QuantityLabelSetting';
import { ObjectTypes } from '~/lib/ObjectTypes';
import DisableWaitlistSetting from '~/components/forms/edit/components/DisableWaitlistSetting';
import InfoMessageBlock from '~/components/InfoMessageBlock.jsx';
import CollapsiblePanel from '~/components/CollapsiblePanel';

const EditSchedulingForm = ({ settings, setSettings }) => {
    const { data: businessInfoData } = useBusinessInfo();
    const { data: customFieldData } = useCustomFieldsSchemaQuery(
        ObjectTypes.BOOKABLE_RESOURCE,
    );
    const { data: eventTypeData } = useEventTypesQuery();

    const hasEventTypeRequiringMultiple =
        eventTypeData?.filter(
            et => et.settings?.numberBookableResourcesRequired > 1,
        ).length > 0;

    let overviewGroupOptions = { 0: 'Names of people/things' };
    if (customFieldData) {
        for (let field of customFieldData) {
            overviewGroupOptions[field.key] =
                `${field.schema.label} field for people/things`;
        }
    }

    const hasQuantity = !!businessInfoData?.hasQuantity;

    return (
        <div>
            <Select
                label="Steps Order"
                className="mt-4"
                inputClassName="max-w-full"
                value={settings?.stepsOrder}
                onChange={value =>
                    setSettings({ ...settings, stepsOrder: value })
                }
                options={{
                    event: 'Event Type > Person/Thing > Date & Time',
                    person: 'Person/Thing > Event Type > Date & Time',
                    resourcelast: 'Event Type > Date & Time > Person/Thing',
                    personcalendar:
                        'Person/Thing > Date & Time > Event Type',
                    monthlyoverviewevent:
                        'Field > Event Type > Time > Person/Thing',
                }}
            />

            {settings?.stepsOrder === 'person' &&
                hasEventTypeRequiringMultiple && (
                    <InfoMessageBlock showIcon={true} className="mt-4">
                        This order is not recommended for event types that
                        require multiple people/things.
                    </InfoMessageBlock>
                )}

            {settings?.stepsOrder === 'monthlyoverviewevent' && (
                <div className="mt-4">
                    <div className="panel-label">Overview Settings</div>
                    <div className="panel p-4">
                        <Select
                            label="In monthly overview display"
                            options={overviewGroupOptions}
                            onChange={newValue =>
                                setSettings({
                                    ...settings,
                                    overviewGroup: newValue,
                                })
                            }
                            value={settings?.overviewGroup}
                        />
                        <div className="text-sm mt-2 ml-3 text-gray-500">
                            First step will show clients a monthly calendar of
                            available days and display the{' '}
                            {(
                                overviewGroupOptions[settings?.overviewGroup] ||
                                overviewGroupOptions[
                                    Object.keys(overviewGroupOptions)[0]
                                ]
                            ).toLowerCase()}{' '}
                            on each day
                        </div>
                    </div>
                </div>
            )}

            <SchedulingFilterTypeSettings
                settings={settings}
                setSettings={setSettings}
            />

            <div className="input-label mt-4">General Options</div>
            <div className="panel p-4">
                <HidePriceDurationSetting
                    settings={settings}
                    setSettings={setSettings}
                />
                <OptionToggle
                    value={settings?.enableRecurring}
                    onChange={value =>
                        setSettings({ ...settings, enableRecurring: value })
                    }
                    className="mt-4"
                    title="Allow Recurring"
                    description="Let clients book multiple times & recurring appointments"
                />
                {settings?.stepsOrder !== 'monthlyoverviewevent' && (
                    <DisableWaitlistSetting
                        setSettings={setSettings}
                        settings={settings}
                        className="mt-4"
                    />
                )}
            </div>

            {settings?.stepsOrder !== 'person' && (
                <>
                    <Select
                        label="Pooled Availability"
                        className="mt-4"
                        inputClassName="max-w-full"
                        value={settings?.pooledAvailability}
                        onChange={value =>
                            setSettings({
                                ...settings,
                                pooledAvailability: value,
                            })
                        }
                        options={{
                            '': 'Clients must pick a specific Person/Thing',
                            optional:
                                'Clients can choose a specific Person/Thing or any available',
                            required:
                                'Hide People/Things, always pick any available',
                        }}
                    />

                    {!!settings?.pooledAvailability && (
                        <div className="panel pl-4 mt-2">
                            <label className="flex">
                                <input
                                    type="radio"
                                    className="mt-4"
                                    checked={
                                        settings?.poolingPrioritization !==
                                        'prioritized'
                                    }
                                    onChange={e =>
                                        e.target.checked &&
                                        setSettings({
                                            ...settings,
                                            poolingPrioritization: 'roundrobin',
                                        })
                                    }
                                />
                                <div className="flex-1 py-3 ml-3 pr-3">
                                    Round Robin
                                    <div className="text-sm text-gray-500">
                                        Evenly distribute bookings on each day
                                    </div>
                                </div>
                            </label>
                            <label className="flex">
                                <input
                                    type="radio"
                                    className="mt-4"
                                    checked={
                                        settings?.poolingPrioritization ===
                                        'prioritized'
                                    }
                                    onChange={e =>
                                        e.target.checked &&
                                        setSettings({
                                            ...settings,
                                            poolingPrioritization:
                                                'prioritized',
                                        })
                                    }
                                />
                                <div className="flex-1 py-3 border-t ml-3 pr-3">
                                    Prioritized
                                    <div className="text-sm text-gray-500">
                                        First available person/thing is booked
                                        based on order in Availability
                                    </div>
                                </div>
                            </label>
                        </div>
                    )}
                </>
            )}

            {hasQuantity && (
                <>
                    <div className="input-label mt-4">Class Options</div>
                    <div className="panel p-4">
                        <HideSpotsLeftSetting
                            setSettings={setSettings}
                            settings={settings}
                        />
                        <QuantityOptionSettings
                            setSettings={setSettings}
                            settings={settings}
                        />
                    </div>
                    <QuantityLabelSetting
                        settings={settings}
                        setSettings={setSettings}
                    />
                </>
            )}

            <CollapsiblePanel
                title="Advanced"
                className="mt-4"
                initialOpen={!!settings?.disableJumpAhead}
            >
                <div className="p-4">
                    <OptionToggle
                        value={settings?.disableJumpAhead}
                        onChange={value =>
                            setSettings({
                                ...settings,
                                disableJumpAhead: value,
                            })
                        }
                        title="Do not jump ahead to next month with availability"
                        description="When enabled, the calendar will stay on the current month even if there is no availability"
                    />
                </div>
            </CollapsiblePanel>
        </div>
    );
};

export default EditSchedulingForm;
